import { useEffect, useMemo, useRef, useState } from 'react';
import type { Extensions } from '@tiptap/react';
import type { HocuspocusProvider } from '@hocuspocus/provider';
import type { Doc as YDoc } from 'yjs';

/**
 * Совместное редактирование статей (фаза 4.7) — клиентская половина.
 *
 * Сервер: Hocuspocus внутри pm2-процесса `franchise-worker`, слушает
 * 127.0.0.1:3002, наружу — только через nginx `location ^~ /ws/wiki`
 * (backend/src/modules/wiki-collab/wiki-collab.service.ts,
 * deploy/SETUP_WS_COLLAB.md). Авторизация — по той же сессионной куке
 * `fib.sid`, что и REST: отдельный токен не нужен, браузер отправляет куку
 * сам (WS same-origin).
 *
 * Три состояния сессии — это главное в этом файле:
 *   • `connecting` — грузим Y.js-модули и ждём первый sync. Редактор в это
 *     время НЕ монтируем: иначе пришлось бы засеять Y.Doc из bodyMd сразу,
 *     и при позднем подключении наш локальный документ склеился бы с
 *     серверным — текст статьи удвоился бы.
 *   • `ready` — sync пришёл: realtime, курсоры соредакторов, общая история.
 *   • `offline` — WS недоступен (в nginx нет блока /ws/wiki/, worker лёг,
 *     сессию не приняли) или не ответил за CONNECT_TIMEOUT_MS. Провайдер
 *     уничтожен, реконнекта нет; редактор монтируется в обычном режиме и
 *     сохраняет через REST. Правки не теряются — просто нет соредакторов.
 *
 * Почему не бесконечный reconnect: человеку надо печатать сейчас. Пробуем
 * один раз, не вышло — работаем как до фазы 4.7.
 *
 * Y.js, Hocuspocus и collab-расширения Tiptap (≈100 КБ gzip) грузятся
 * ДИНАМИЧЕСКИ — только при входе в режим правки: читателю статьи они не
 * нужны и в основной бандл попадать не должны. Готовые расширения хук
 * отдаёт в `extensions` — собрать их здесь дешевле, чем тащить статический
 * импорт в TiptapEditor.
 */

/** Сколько ждём первый sync, прежде чем уйти в офлайн. */
const CONNECT_TIMEOUT_MS = 4000;

/** Пауза перед засевом пустого Y.Doc — см. seedCollabDoc. */
const SEED_SETTLE_MS = 700;

/** Имя Y.js-документа — формат, который парсит сервер (`wiki:<uuid>`). */
const DOC_PREFIX = 'wiki:';

/** Поле Y.Doc, с которым работает @tiptap/extension-collaboration. */
export const COLLAB_FIELD = 'default';

export type CollabState = 'connecting' | 'ready' | 'offline';

/** Соредактор в комнате (из awareness). Себя в список не кладём. */
export interface CollabPeer {
  clientId: number;
  name: string;
  color: string;
}

export interface CollabUser {
  name: string;
  color: string;
}

export interface CollabSession {
  state: CollabState;
  /** Y.Doc и провайдер — только когда state === 'ready'. */
  ydoc: YDoc | null;
  provider: HocuspocusProvider | null;
  /** Готовые Collaboration + CollaborationCursor; пусто, если не 'ready'. */
  extensions: Extensions;
  /** Кто ещё в комнате, кроме нас. */
  peers: CollabPeer[];
  /** Наши имя и цвет — их же рисует CollaborationCursor соседям. */
  user: CollabUser;
}

/**
 * Цвет курсора из имени: стабильный (у человека всегда один и тот же) и из
 * брендовой палитры, чтобы курсоры не спорили с оранжевым интерфейсом.
 */
const CURSOR_COLORS = [
  '#FF6900', // brand orange
  '#0096FF', // info
  '#7CB342', // success
  '#7E3FB8', // tip violet
  '#FB8C00', // warning
  '#00A6A6', // teal
  '#E53935', // error red
  '#3F51B5', // indigo
];

export function colorForName(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return CURSOR_COLORS[Math.abs(hash) % CURSOR_COLORS.length];
}

/** wss:// для https-страницы, ws:// для http (локальная разработка). */
function collabWsUrl(): string {
  const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${window.location.host}/ws/wiki`;
}

/**
 * Y.Doc без текста: ни одного узла или только пустые абзацы. Пустой абзац остаётся, когда редактор
 * открыли на пустом документе и он записал в него стартовый `<p></p>` (16.10.2026) — такой документ
 * тоже надо засеять, иначе статья откроется пустой. Без статического импорта yjs (он в ленивом чанке):
 * у Y.XmlElement есть nodeName и length (число детей), у Y.XmlText nodeName нет.
 */
export function collabFragmentIsEmpty(fragment: { toArray: () => unknown[] }): boolean {
  return fragment.toArray().every((n) => {
    const el = n as { nodeName?: unknown; length?: unknown };
    return el.nodeName === 'paragraph' && el.length === 0;
  });
}

/**
 * Первичное наполнение пустого Y.Doc текстом статьи из БД.
 *
 * Y.Doc пуст в двух случаях: статью ещё не правили совместно, либо
 * `yjs_state` сбросил бэкенд после REST-сохранения (см. updateArticle в
 * wiki-editor.service.ts) — тогда истина снова в body_md.
 *
 * Сеет РОВНО ОДИН клиент: тот, у кого минимальный awareness clientID среди
 * присутствующих. Остальные ждут — так текст не удваивается при
 * одновременном входе двух человек. Если «главный» ушёл не засеяв,
 * следующая проверка выберет нового минимального.
 */
export function seedCollabDoc(
  ydoc: YDoc,
  /** Минимум, который нужен от провайдера — так функция тестируется без WS. */
  provider: { awareness: { clientID: number; getStates: () => Map<number, unknown> } | null },
  seed: () => void,
  /**
   * Пауза перед первой попыткой. Сразу после sync клиент ещё может не знать о
   * соседе, открывшем статью в ту же секунду: оба видят себя «минимальными» и
   * оба сеют — текст удваивается (поймано на стенде 07.10.2026). За паузу
   * доходят и awareness соседа, и его засев; дальше решает минимальный clientID.
   */
  settleMs = SEED_SETTLE_MS,
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;

  const attempt = (): void => {
    if (stopped) return;
    // Кто-то уже засеял (наш или чужой апдейт) — больше делать нечего.
    if (!collabFragmentIsEmpty(ydoc.getXmlFragment(COLLAB_FIELD))) return;

    const awareness = provider.awareness;
    const me = awareness?.clientID ?? 0;
    const ids = awareness ? Array.from(awareness.getStates().keys()) : [];
    const minId = ids.length > 0 ? Math.min(...ids, me) : me;
    if (minId !== me) {
      // Не наша очередь: сеет клиент с меньшим id. Перепроверим — вдруг он
      // отключился, тогда минимальным станем мы.
      timer = setTimeout(attempt, 1200);
      return;
    }
    seed();
  };

  // Без awareness (сокет умер) соседей быть не может — сеем сразу.
  if (!provider.awareness || settleMs <= 0) attempt();
  else timer = setTimeout(attempt, settleMs);
  return () => {
    stopped = true;
    if (timer) clearTimeout(timer);
  };
}

/** Стабильная пустая ссылка — чтобы не пересобирать расширения редактора. */
const NO_EXTENSIONS: Extensions = [];

/** Метка соседа на его каретке и стрелке мыши (collab-pointers.ts) — по ней ищем, куда прокрутить. */
export const COLLAB_CARET_ATTR = 'data-collab-client';

/**
 * Каретка соседа — как у CollaborationCursor по умолчанию, плюс clientId из
 * awareness (y-prosemirror передаёт его вторым аргументом), чтобы к ней можно
 * было прокрутить из плашки «Сейчас правят вместе с вами».
 */
function renderCaret(user: { name?: string; color?: string }, clientId?: number): HTMLElement {
  const color = user.color && /^#[0-9a-f]{6}$/i.test(user.color) ? user.color : '#FF6900';
  const caret = document.createElement('span');
  caret.classList.add('collaboration-cursor__caret');
  caret.style.borderColor = color;
  if (clientId != null) caret.setAttribute(COLLAB_CARET_ATTR, String(clientId));
  const label = document.createElement('div');
  label.classList.add('collaboration-cursor__label');
  label.style.backgroundColor = color;
  label.textContent = user.name ?? '';
  caret.appendChild(label);
  return caret;
}

/**
 * Прокрутить к месту, где сейчас сосед: сначала каретка (где он печатает),
 * иначе его стрелка мыши. false — его нет в тексте (вкладка в фоне, мышь вне статьи).
 * Здесь, а не в collab-pointers.ts: ArticlePage импортирует это статически, а
 * collab-pointers тянет yjs — ему место в ленивом чанке.
 */
export function scrollToCollabPeer(clientId: number): boolean {
  const caret = document.querySelector<HTMLElement>(`.collaboration-cursor__caret[${COLLAB_CARET_ATTR}="${clientId}"]`);
  const pointer = document.querySelector<HTMLElement>(`.fk-collab-pointer[${COLLAB_CARET_ATTR}="${clientId}"]`);
  const target = caret ?? pointer;
  if (!target) return false;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  // Показать имя, даже если ярлык каретки уже погас.
  target.classList.remove('is-found');
  void target.offsetWidth;
  target.classList.add('is-found');
  window.setTimeout(() => target.classList.remove('is-found'), 2200);
  return true;
}

/**
 * Поднимает сессию совместного редактирования статьи.
 *
 * @param articleId  UUID статьи; без него хук ничего не делает.
 * @param enabled    Включать ли collab (режим правки + `collabEnabled` из
 *                   /wiki-auth/me). false → сразу 'offline', без сокета.
 * @param userName   Имя для курсора и списка «сейчас правят».
 */
export function useCollab(
  articleId: string | null | undefined,
  enabled: boolean,
  userName: string,
): CollabSession {
  const [state, setState] = useState<CollabState>('offline');
  const [peers, setPeers] = useState<CollabPeer[]>([]);
  // Живые объекты сессии — в state, а не в ref: TiptapEditor должен
  // пересобрать расширения ровно один раз, когда они появились.
  const [live, setLive] = useState<{
    ydoc: YDoc;
    provider: HocuspocusProvider;
    extensions: Extensions;
  } | null>(null);

  const user = useMemo<CollabUser>(
    () => ({ name: userName || 'Без имени', color: colorForName(userName || '?') }),
    [userName],
  );
  // Имя может подгрузиться позже сокета — читаем его через ref в момент
  // конфигурации курсора, а не фиксируем в зависимостях эффекта.
  const userRef = useRef(user);
  userRef.current = user;

  useEffect(() => {
    if (!enabled || !articleId) {
      setState('offline');
      setPeers([]);
      setLive(null);
      return;
    }

    setState('connecting');
    let cancelled = false;
    let cleanup: (() => void) | null = null;

    void (async () => {
      const [{ HocuspocusProvider, HocuspocusProviderWebsocket }, Y, collabExt, cursorExt, pointersExt] =
        await Promise.all([
          import('@hocuspocus/provider'),
          import('yjs'),
          import('@tiptap/extension-collaboration'),
          import('@tiptap/extension-collaboration-cursor'),
          import('./collab-pointers'),
        ]);
      if (cancelled) return;

      const ydoc = new Y.Doc();
      // Сокет создаём явно, чтобы ограничить попытки: без блока /ws/wiki/ в
      // nginx провайдер иначе уходит в вечный reconnect-цикл и молотит
      // запросами всё время, пока открыт редактор.
      const socket = new HocuspocusProviderWebsocket({
        url: collabWsUrl(),
        maxAttempts: 1,
        messageReconnectTimeout: 30_000,
      });
      const provider = new HocuspocusProvider({
        websocketProvider: socket,
        name: `${DOC_PREFIX}${articleId}`,
        document: ydoc,
      });
      // Hocuspocus 4: с переданным websocketProvider провайдер сам к сокету НЕ
      // подключается (manageSocket=false) — без attach() сокет открыт, но ни
      // авторизации, ни sync по документу не уходит, и через CONNECT_TIMEOUT_MS
      // редактор молча уходит в офлайн. Так было с 03.09 по 07.10.2026.
      provider.attach();

      let settled = false;
      let timer: ReturnType<typeof setTimeout> | null = null;

      const destroy = (): void => {
        if (timer) clearTimeout(timer);
        try {
          provider.destroy();
        } catch {
          /* провайдер мог не успеть подключиться — уничтожать нечего */
        }
        try {
          socket.destroy();
        } catch {
          /* тот же случай */
        }
        ydoc.destroy();
      };

      /** Уйти в офлайн: убить сокет, оставить редактор без realtime. */
      const giveUp = (reason: string): void => {
        if (settled) return;
        settled = true;
        console.info(`[collab] офлайн-режим: ${reason}`);
        destroy();
        setState('offline');
        setPeers([]);
        setLive(null);
      };

      const onSynced = (): void => {
        if (settled) return;
        settled = true;
        if (timer) clearTimeout(timer);
        setLive({
          ydoc,
          provider,
          extensions: [
            collabExt.default.configure({ document: ydoc, field: COLLAB_FIELD }),
            cursorExt.default.configure({ provider, user: userRef.current, render: renderCaret }),
            // Стрелки мыши соседей с именем (владелец 07.10.2026).
            pointersExt.CollabPointers.configure({ provider }),
          ],
        });
        setState('ready');
      };

      // Список соредакторов: себя отфильтровываем по clientID awareness'а.
      const onAwareness = (): void => {
        const awareness = provider.awareness;
        if (!awareness) return;
        const out: CollabPeer[] = [];
        awareness.getStates().forEach((st, clientId) => {
          if (clientId === awareness.clientID) return;
          const u = (st as { user?: Partial<CollabUser> } | undefined)?.user;
          if (!u?.name) return;
          out.push({ clientId, name: u.name, color: u.color || colorForName(u.name) });
        });
        setPeers(out);
      };

      // Сокет закрылся до первого sync — значит подключиться не вышло
      // (нет блока /ws/wiki/ в nginx, worker лёг). Повторов нет
      // (maxAttempts: 1), так что ждать таймаут смысла нет: уходим в офлайн
      // сразу, чтобы человек не смотрел 4 секунды на «Подключаемся…».
      const onClosed = (): void => giveUp('соединение закрыто');

      timer = setTimeout(() => giveUp('сервер не ответил'), CONNECT_TIMEOUT_MS);
      provider.on('synced', onSynced);
      provider.on('authenticationFailed', () => giveUp('сессия не принята сервером'));
      provider.on('close', onClosed);
      provider.on('awarenessUpdate', onAwareness);
      provider.on('awarenessChange', onAwareness);

      cleanup = () => {
        settled = true;
        provider.off('synced', onSynced);
        provider.off('close', onClosed);
        provider.off('awarenessUpdate', onAwareness);
        provider.off('awarenessChange', onAwareness);
        destroy();
      };
      // Размонтировались, пока грузились модули — закрываем сразу.
      if (cancelled) {
        cleanup();
        cleanup = null;
      }
    })();

    return () => {
      cancelled = true;
      cleanup?.();
      cleanup = null;
      setLive(null);
    };
    // userName влияет только на awareness (его пишет CollaborationCursor
    // через userRef) — пересоздавать сокет из-за смены имени не нужно.
  }, [articleId, enabled]);

  return {
    state,
    ydoc: live?.ydoc ?? null,
    provider: live?.provider ?? null,
    extensions: live?.extensions ?? NO_EXTENSIONS,
    peers: live ? peers : [],
    user,
  };
}
